import { formatDisplayableTime, convertFormattedStringIntoSeconds, customColumnSanitize } from '@/helpers/commonFunctions';
import { SHOW_TYPES, STANDARD_EVENT_KEYS, SHOW_TYPES_BY_EVENTS, EVENT_EDIT_METHODS, ADD_EVENT_BUTTON_STATES, EVENT_TABLE_COLUMNS, SHOW_EQUIPMENT_MANUFACTURES, PRODUCTS, MAX_NUMBER_OF_FREE_TIER_EVENTS, METHOD_TO_EDIT_TIME, EVENT_ISSUE_TYPE_LABELS, EVENT_ISSUE_TYPE_LABEL_FALLBACK } from '@/constants';
import { createUniqueID } from '@/helpers/createID';
import { shortenDisplayTime } from './shortenTime';
import { useSettingStore } from '@/stores/setting';
import { toDisplayableTime } from '@/helpers/commonFunctions';
import { useShowStore } from '@/stores/show';
import { toSeconds } from './toSeconds';
import { useAuthStore } from '@/stores/auth';
import { NotificationCenter, NotificationHandler, NotificationType } from '@/assets/libraries/NotificationHandler';
import { useShowOperatorStore } from '@/stores/showOperatorStore';
import { getFirmwareLimits } from '@/helpers/firmwareLimits';

let used_cues = {};
let channel_and_cues = [];
let used_cues_per_channel = {};
let number_of_cues_used_for_channel = {};
let max_number_of_times_a_cue_can_be_refired = 1;
let max_number_of_events_per_channel = 400;

export function resetValuesUsedForChannelCueCalculations(firmware_version) {
	used_cues = {};
	channel_and_cues = [];
	used_cues_per_channel = {};
	number_of_cues_used_for_channel = {};

	const override_max_number_of_refires_for_cue = useSettingStore().settings.max_number_of_refires_for_cue;
	if (override_max_number_of_refires_for_cue != undefined && override_max_number_of_refires_for_cue > 0) {
		max_number_of_times_a_cue_can_be_refired = override_max_number_of_refires_for_cue;
	} else {
		if (firmware_version != undefined && firmware_version >= 6.0) {
			max_number_of_times_a_cue_can_be_refired = undefined;
		} else {
			max_number_of_times_a_cue_can_be_refired = 1;
		}
	}

	if (firmware_version != undefined && firmware_version >= 6.0) {
		max_number_of_events_per_channel = 400;
	} else {
		max_number_of_events_per_channel = undefined;
	}
}

// Pyro cues are always numbered 1-18, regardless of firmware version.
export const MAX_PYRO_CUE = 18;

export function getIndexOfEventToBeAdded(timeline_event_time_of_event_to_be_added, events, selected_rows) {
	const index_of_event_id = returnIndexOfEventIds(events);
	const length_of_events = events.length;
	const show_store = useShowStore();
	const events_without_alternate_events = events.filter((event) => event.isALTERNATE1 != true && event.isALTERNATE2 != true);

	// If no non-alternate events exist return index as 0
	if (events_without_alternate_events.length == 0) {
		return 0;
	}

	for (let index = 0; index < length_of_events; index++) {
		const current_event = events[index];
		const current_event_timeline_time = getDecimalPlaces(current_event.timeline_event_time, 2);
		const last_event_without_alternate_timeline_event_time = getDecimalPlaces(events_without_alternate_events[events_without_alternate_events.length - 1].timeline_event_time, 2);
		// const selected_row_event_index = index_of_event_id[selected_rows[0]];
		const selected_row_event_index = show_store.current_selected_row_index_for_pasting > -1 ? index_of_event_id[selected_rows[show_store.current_selected_row_index_for_pasting]] : index_of_event_id[selected_rows[0]];
		if (current_event_timeline_time > timeline_event_time_of_event_to_be_added) {
			if (index < selected_row_event_index && selected_rows.length > 0 && selected_row_event_index.timeline_event_time < timeline_event_time_of_event_to_be_added) {
				continue;
			}

			if (current_event.isALTERNATE1 == true || current_event.isALTERNATE2 == true) {
				return events_without_alternate_events.length;
			}

			return index;
		} else if (current_event_timeline_time == timeline_event_time_of_event_to_be_added) {
			if (index < selected_row_event_index) {
				continue;
			}

			if (current_event.isALTERNATE1 == true || current_event.isALTERNATE2 == true) {
				return events_without_alternate_events.length;
			}

			if (index + 1 <= length_of_events - 1) {
				const next_event = events[index + 1];
				const next_event_timeline_event_time = getDecimalPlaces(next_event.timeline_event_time, 2);
				if (next_event_timeline_event_time == current_event_timeline_time && next_event.isSTEP == false) {
					continue;
				}
			}
			return index + 1;
		} else if (timeline_event_time_of_event_to_be_added > last_event_without_alternate_timeline_event_time) {
			// if the timeline_event_time_of_event_to_be_added is greater then the timeline_event_time of all timed events i.e pyro + dmx events
			return events_without_alternate_events.length;
		}
	}
}

export function getDecimalPlaces(number, decimalPlaces) {
	let toReturn = '';
	if ((number + '').indexOf('.') == -1) {
		return number;
	}
	let splitNumber = (number + '').split('.');
	toReturn = splitNumber[0] + '.';
	let decimals = splitNumber[1];
	for (let i = 0, len = decimals.length; i < len && i < decimalPlaces; i++) {
		toReturn += decimals.charAt(i);
	}
	return parseFloat(toReturn);
}

export function calculateEventTimeWithMethod({ selected_method, event_id, events = [], min_input_value = '00', sec_input_value = '00', mil_sec_input_value = '00', audio_duration, delay_sec_input_value = '00', method_used_to_edit_time } = {}) {
	const event_index = events.findIndex((e) => event_id === e.event_id);
	const event = events[event_index];

	if (event == undefined) {
		return;
	}

	// Add the method used to edit the time for debugging purposes
	if (method_used_to_edit_time != undefined) {
		event['method_used_to_edit_time'] = method_used_to_edit_time;
	}

	const previous_event = events[event_index - 1];

	if (selected_method === EVENT_EDIT_METHODS.custom) {
		let newTime = min_input_value + ':' + sec_input_value + '.' + mil_sec_input_value;
		let newTimeInSeconds = convertFormattedStringIntoSeconds(newTime);
		let newTimeFormatted = formatDisplayableTime('' + newTimeInSeconds);

		if (newTimeFormatted.indexOf('NaN') != -1) {
			return { 'event_time': 'Non Number Inputted', 'index': event_index };
		}

		const show_store = useShowStore();

		// check if show is non audio
		if (show_store.info_about_loaded_song != undefined && (newTimeInSeconds < 0 || newTimeInSeconds > audio_duration)) {
			return { 'event_time': 'Out Of Bounds', 'index': event_index };
		}

		return { 'event_time': newTimeFormatted, 'index': event_index };
	} else if (selected_method === EVENT_EDIT_METHODS.add_to_previous_duration) {
		let previous_event_start_time = previous_event?.timeline_event_time != undefined ? previous_event.timeline_event_time : 0;
		let previous_duration = previous_event?.duration_in_seconds != undefined ? previous_event.duration_in_seconds : 0;

		let seconds_to_add = 0;
		if (delay_sec_input_value != undefined) {
			seconds_to_add = toSeconds(delay_sec_input_value);
		}

		let addedTime = 0.0;

		if (previous_event != undefined) {
			addedTime = previous_event_start_time + (seconds_to_add + previous_duration);
		} else {
			addedTime = previous_event_start_time + seconds_to_add;
		}

		addedTime = addedTime.decimal;

		let formattedAddedTime = formatDisplayableTime(addedTime + '');

		if (formattedAddedTime.indexOf('NaN') != -1) {
			return { 'event_time': 'Non Number Inputted', 'index': event_index };
		}

		const show_store = useShowStore();
		// check if show is non audio
		if (show_store.info_about_loaded_song != undefined && (addedTime < 0 || addedTime > audio_duration)) {
			return { 'event_time': 'Out Of Bounds', 'index': event_index };
		}

		return { 'event_time': formattedAddedTime, 'index': event_index };
	} else if (selected_method === EVENT_EDIT_METHODS.step) {
		console.log('STEP');
		return { 'event_time': 'STEP', 'index': event_index };
	} else if (selected_method === EVENT_EDIT_METHODS.alternate || selected_method === EVENT_EDIT_METHODS.alternate1) {
		return { 'event_time': 'ALTERNATE', 'index': event_index };
	} else if (selected_method === EVENT_EDIT_METHODS.alternate2) {
		return { 'event_time': 'ALTERNATE2', 'index': event_index };
	} else {
		console.log('invalid method type', selected_method);
	}
}

export function returnIndexOfEventIds(events = useShowStore()?.active_show?.events) {
	let index_of_event_id = {};
	for (let index = 0; index < events.length; index++) {
		let event = events[index];
		index_of_event_id[event.event_id] = index;
	}
	return index_of_event_id;
}

export function changeEventValueInArray(index, event_key, new_value, event_keys, events) {
	if (typeof new_value == 'string' && new_value.replace(/\s/g, '') == '') {
		new_value = undefined;
	}

	if (STANDARD_EVENT_KEYS[event_key] == undefined) {
		//Custom Column

		let keys = event_keys;

		if (events[index].custom_values == undefined) {
			var newCustomArray = {};
			newCustomArray[keys] = new_value;
			events[index].custom_values = newCustomArray;
		} else {
			events[index].custom_values[keys] = new_value;
		}
		return;
	}

	let universe_int = undefined;
	let channel_int = undefined;
	let event_int = undefined;
	let duration_in_seconds = undefined;

	switch (event_key) {
		case 'universe':
			universe_int = parseInt(new_value);
			events[index]['universe'] = isNaN(universe_int) == false ? universe_int : new_value;
			break;
		case 'channel':
			channel_int = parseInt(new_value);
			events[index]['channel'] = isNaN(channel_int) == false ? channel_int : new_value;
			break;
		case 'event':
			event_int = parseInt(new_value);
			events[index]['event'] = isNaN(event_int) == false ? event_int : new_value;
			break;
		case 'duration':
			events[index]['duration'] = new_value;
			duration_in_seconds = new_value != undefined ? convertFormattedStringIntoSeconds(new_value) : undefined;
			events[index]['duration_in_seconds'] = duration_in_seconds;
			break;

		case 'igniter_pre_fire':
			events[index]['igniter_pre_fire'] = new_value;
			events[index]['igniter_pre_fire_in_seconds'] = convertFormattedStringIntoSeconds(getSafeString(new_value));
			break;

		case 'lift_time':
			events[index]['lift_time'] = new_value;
			events[index]['lift_time_in_seconds'] = convertFormattedStringIntoSeconds(getSafeString(new_value));
			break;

		case 'pulse':
			events[index]['pulse'] = convertFormattedStringIntoSeconds(getSafeString(new_value));
			break;

		default:
			events[index][event_key] = new_value;
	}

	return events;
}

export function calculateEventTimes(events_array = undefined, firmware_version, { start_from_row_index, end_at_row_index } = {}) {
	// if (!Array.isArray(events_array)) {
	// 	console.warn("[Calculate Event Times (All)] Passed Argument is not of type 'Array'");
	// 	return [];
	// }
	// var adjusted_array = [];
	let stored_timeline_event_time = 0;
	// to calculate relative_event_time till the end starting from the updated row.
	if (start_from_row_index != undefined) {
		for (let i = start_from_row_index; i < events_array.length; i++) {
			if (end_at_row_index != undefined && end_at_row_index == i) {
				break;
			}

			const event = events_array[i];
			if (event.isSTEP == true) {
				stored_timeline_event_time = event.timeline_event_time;
			}

			const adjusted_times = calculateEventTimeForEvent(event, firmware_version, stored_timeline_event_time);
			if (!adjusted_times) return null;

			event.calculated_event_time = adjusted_times.event_time;
			event.calculated_event_time_in_seconds = adjusted_times.event_time_in_seconds;
			event.negative_calculated_event_time = adjusted_times.negative_calculated_time;

			event.relative_event_time = adjusted_times.relative_event_time;
			event.relative_event_time_in_seconds = adjusted_times.relative_event_time_in_seconds;
		}
		return;
	}

	const adjusted_array = events_array
		.map((event) => {
			if (event.isSTEP == true) {
				stored_timeline_event_time = event.timeline_event_time;
			}

			const adjusted_times = calculateEventTimeForEvent(event, firmware_version, stored_timeline_event_time);
			if (!adjusted_times) return null;

			event.calculated_event_time = adjusted_times.event_time;
			event.calculated_event_time_in_seconds = adjusted_times.event_time_in_seconds;
			event.negative_calculated_event_time = adjusted_times.negative_calculated_time;

			event.relative_event_time = adjusted_times.relative_event_time;
			event.relative_event_time_in_seconds = adjusted_times.relative_event_time_in_seconds;

			return event;
		})
		.filter((event) => event !== null);

	return adjusted_array;
}

export function calculateEventTimeForEvent(event, firmware_version, stored_timeline_event_time = undefined) {
	if (!event) {
		// console.warn('[Calculate Event Time] Error with event!');
		return undefined;
	}
	// console.warn('[Calculate Event Time] event:', event);

	if (event.isSTEP) {
		return { event_time: 'STEP', event_time_in_seconds: 0, relative_event_time: 'STEP', relative_event_time_in_seconds: 0 };
	}

	// if event is isALTERNATE, then set isALTERNATE1 to true, as isALTERNATE is no longer used for new shows, but might exist in old shows
	// as isALTERNATE is always == to isALTERNATE1
	if (event.isALTERNATE || event.isALTERNATE1) {
		event.isALTERNATE1 = true;
		return { event_time: 'ALTERNATE', event_time_in_seconds: 0, relative_event_time: 'ALTERNATE', relative_event_time_in_seconds: 0 };
	}
	if (event.isALTERNATE2) {
		return { event_time: 'ALTERNATE2', event_time_in_seconds: 0, relative_event_time: 'ALTERNATE2', relative_event_time_in_seconds: 0 };
	}
	let { event_time_in_seconds: un_adjusted_event_time, igniter_pre_fire_in_seconds, lift_time_in_seconds } = event;
	// if stored_timeline_event_time is not undefined, then we are in the middle of a step event, so we need to adjust the time accordingly WITHOUT EFFECTING THE event_time_in_seconds
	if (stored_timeline_event_time != undefined) {
		un_adjusted_event_time = un_adjusted_event_time - stored_timeline_event_time;
	}

	if (isNaN(igniter_pre_fire_in_seconds) == false && typeof igniter_pre_fire_in_seconds == 'string') {
		igniter_pre_fire_in_seconds = parseFloat(igniter_pre_fire_in_seconds);
	}
	if (isNaN(lift_time_in_seconds) == false && typeof lift_time_in_seconds == 'string') {
		lift_time_in_seconds = parseFloat(lift_time_in_seconds);
	}
	const pre_fire = igniter_pre_fire_in_seconds ? parseFloat(igniter_pre_fire_in_seconds.toFixed(3)) : 0;
	const lift_time = lift_time_in_seconds ? parseFloat(lift_time_in_seconds.toFixed(3)) : 0;

	if (isNaN(pre_fire) || isNaN(lift_time)) {
		// console.warn('[Calculate Event Time] pre_fire or lift_time are NaN', pre_fire, lift_time);
	}

	const raw_adjusted_event_time = un_adjusted_event_time - (pre_fire + lift_time);
	const negative_time = raw_adjusted_event_time < 0;
	let adjusted_event_time = Math.max(raw_adjusted_event_time, 0);

	const decimal_places = firmware_version >= 5 ? 2 : 1;
	const formatted_event_time = toDisplayableTime(adjusted_event_time.toFixed(decimal_places), decimal_places);

	let relative_event_time_in_seconds = parseFloat(Math.max(un_adjusted_event_time, 0).toFixed(decimal_places));
	let formatted_relative_event_time = toDisplayableTime(relative_event_time_in_seconds, decimal_places);

	return { event_time: formatted_event_time, event_time_in_seconds: adjusted_event_time, negative_calculated_time: negative_time, relative_event_time: formatted_relative_event_time, relative_event_time_in_seconds };
}

function getSafeString(string) {
	if (string == undefined || string == null) {
		return '';
	}
	return string.toString();
}

export function sortEvents(events) {
	if (events == undefined || Array.isArray(events) == false) {
		if (events.length <= 1) {
			return events;
		}
		return [];
	}
	events.sort((a, b) => {
		if (a.isALTERNATE1 || a.isALTERNATE2) {
			if (b.isALTERNATE2) {
				return -1;
			}
			// if a event is alternate, it should come AFTER b event,  so return 1
			return 1;
		} else if (b.isALTERNATE1 || b.isALTERNATE2) {
			if (a.isALTERNATE2) {
				return 1;
			}
			// if b event is alternate, a event should come before b event,  so return -1
			return -1;
		} else if (a.timeline_event_time !== b.timeline_event_time) {
			return a.timeline_event_time - b.timeline_event_time;
		} else if (a.isSTEP && !b.isSTEP) {
			// not applicable any more: means same timeline_event_time, but b event is not a step and a event is a step
			// so a should come AFTER b event,  so return 1
			return 0; // no need to sort step events having same time.
		} else if (!a.isSTEP && b.isSTEP) {
			// means same timeline_event_time, but b event is a step and b event is not a step
			// so a should come BEFORE b event,  so return -1
			return 0; // no need to sort step events having same time.
		} else {
			return 0;
		}
	});
	return events;
}

export function correctTimelineEventTime(events) {
	let add_previous_STEPS_duration = true;
	let previous_timeline_event_time = 0;

	for (let i = 0; i < events.length; i++) {
		const event = events[i];

		if (event.event_time_in_seconds == undefined || event.event_time_in_seconds < 0) {
			if (event.isSTEP == true) {
				console.warn('[Correct Timeline Event Time] Found NEGATIVE event time on STEP event replacing with 00:00.00.');
				event.event_time_in_seconds = 0;
			} else {
				console.error('[Correct Timeline Event Time] Aborting correcting. Event time is not vaild');
				return;
			}
		}

		if (event.isSTEP == true) {
			if (i > 0 && events[i - 1].duration_in_seconds != undefined) {
				previous_timeline_event_time = previous_timeline_event_time + events[i - 1].duration_in_seconds;
				console.log('Added previous events duration to step');
			}

			events[i].timeline_event_time = previous_timeline_event_time;

			continue;
		}
		if (add_previous_STEPS_duration == true && i > 0 && events[i - 1].isSTEP == true && events[i - 1].duration_in_seconds != undefined) {
			previous_timeline_event_time += events[i - 1].duration_in_seconds;
			console.log('added previous steps duration');
		}
		previous_timeline_event_time = event.event_time_in_seconds;
		events[i].timeline_event_time = previous_timeline_event_time;

		events[i].timeline_event_time = event.event_time_in_seconds;
	}

	return events;
}

export function shiftEvents(time_to_shift_events, events, selected_rows, method_used_to_edit_time = undefined) {
	// TODO: Error handling
	// if (time_to_shift_events == undefined || null) { NotificationCenter.add(new Notification("Error Shifting Cues", "There was an error trying to shift the selected cues.", [], NotificationType.WARNING)); console.log('[Shift Cues] Undefined time to shift by', time_to_shift_events); }
	const index_of_event_id = returnIndexOfEventIds(events);

	let indexes = selected_rows.map((event_id) => {
		return index_of_event_id[event_id];
	});

	for (const event_index of indexes) {
		let event = events[event_index];

		let new_event_time = event.event_time_in_seconds + time_to_shift_events;

		if (new_event_time < 0) {
			// TODO: Error handling
			console.log('[Shift Cues] Undefined time to shift by', time_to_shift_events);
			continue;
		}

		events[event_index].event_time_in_seconds = new_event_time;
		events[event_index].event_time = formatDisplayableTime(new_event_time + '');
		events[event_index].timeline_event_time = new_event_time;
		// Add the method used to edit the time for debugging purposes
		if (method_used_to_edit_time != undefined) {
			events[event_index]['method_used_to_edit_time'] = method_used_to_edit_time;
		}

		updateOffsetsAndRelativeTime({ updated_event: events[event_index] });
	}

	// EVS-1147: undo is owned by makeEventChanges → postEventChanges (single gesture = one stack entry)
	return events;
}

export function insertTimedEventAbove(events, event, show_type, add_event_button_current_state, selected_rows, current_wave_time, event_type = null) {
	if (event == undefined) {
		console.warn('[Event Table][Unselect Row] Error getting {Event Object}!');
		return;
	}
	const index_of_event_id = returnIndexOfEventIds(events);

	let index_to_be_above = index_of_event_id[event.event_id];

	let event_time = event.timeline_event_time;
	let make_STEP = event.isSTEP;
	let make_ALTERNATE1 = event.isALTERNATE1;
	let make_ALTERNATE2 = event.isALTERNATE2;

	// if (make_ALTERNATE1 || make_ALTERNATE2) {
	// 	event_time = 0;
	// }

	let index_new_event_added_to = undefined;

	if (show_type === SHOW_TYPES.PYRO_DMX.key || show_type === SHOW_TYPES.DMX.key) {
		let new_event_type = add_event_button_current_state;
		if (event_type != undefined) {
			new_event_type = event_type;
		} else if (new_event_type == ADD_EVENT_BUTTON_STATES.STEP && selected_rows[0] != undefined && index_of_event_id[selected_rows[0]] != undefined && events[index_of_event_id[selected_rows[0]]].isDMX == true) {
			//Pyro is already the default so only need to account for DMX
			//If clicking on a DMX event while the selected new event button is STEP insert a DMX event.
			new_event_type = ADD_EVENT_BUTTON_STATES.DMX;
		}
		const new_event_object = {
			show_type,
			current_wave_time,
			events,
			selected_rows,
			time: event_time,
			leave_time_blank: false,
			make_STEP_event: make_STEP,
			make_DMX_event: new_event_type == ADD_EVENT_BUTTON_STATES.DMX,
			make_ALTERNATE1,
			make_ALTERNATE2,
		};
		const { index, updated_events } = addNewEventToTable(new_event_object); //event time, is blank event, is step event, is dmx event
		index_new_event_added_to = index;
		events = updated_events;
	} else {
		const new_event_object = {
			show_type,
			current_wave_time,
			events,
			selected_rows,
			time: event_time,
			leave_time_blank: false,
			make_STEP_event: make_STEP,
			make_ALTERNATE1,
			make_ALTERNATE2,
		};
		const { index, updated_events } = addNewEventToTable(new_event_object); //event time, is blank event, is step event, is dmx event
		index_new_event_added_to = index;
		events = updated_events;
	}

	// method to remove an element from an array and then store that removed element in a variable.
	let new_event = events.splice(index_new_event_added_to, 1)[0];

	if (index_to_be_above < 0 || new_event == undefined || new_event == null) {
		return;
	}

	new_event.is_inserted_event = true;

	new_event.timeline_event_time = events[index_to_be_above].timeline_event_time; //Removed to simply; In some shows it may be a little more inefficent 7/9/2020

	// method to insert an element into an array at a specific position without removing any existing elements.
	events.splice(index_to_be_above, 0, new_event);

	return { events, added_event_id: new_event.event_id };
}

export function insertTimedEventBelow(events, event, show_type, add_event_button_current_state, selected_rows, current_wave_time, event_type = null, dropped_to_waveform_event_time = undefined) {
	if (event == undefined) {
		console.warn('[Cue Table][Unselect Row] Error getting {Cue Object}!');
		return;
	}

	const index_of_event_id = returnIndexOfEventIds(events);

	let index_to_be_below = index_of_event_id[event.event_id] + 1; //+1 was added to fix a bug where it was below two below instead of 1; Have no idea how it got past production for so long. 5/21/2020; Therory is that the .getParentCue() got more accurate over time, resulting in the insert cue being placed below instead of above; -1 was to account for this; Moved the index_to_be_below to before addNewCue();

	let event_time = dropped_to_waveform_event_time ?? event.timeline_event_time;
	let make_STEP = event.isSTEP;

	let index_new_event_added_to = undefined;

	if (show_type === SHOW_TYPES.PYRO_DMX.key || show_type === SHOW_TYPES.DMX.key) {
		let new_event_type = add_event_button_current_state;
		if (event_type != undefined) {
			new_event_type = event_type;
		} else if (new_event_type == ADD_EVENT_BUTTON_STATES.STEP && selected_rows[0] != undefined && index_of_event_id[selected_rows[0]] != undefined && events[index_of_event_id[selected_rows[0]]].isDMX == true) {
			//Pyro is already the default so only need to account for DMX
			//If clicking on a DMX event while the selected new event button is STEP insert a DMX event.
			new_event_type = ADD_EVENT_BUTTON_STATES.DMX;
		}
		const new_event_object = {
			show_type,
			current_wave_time,
			events,
			selected_rows,
			time: event_time,
			leave_time_blank: false,
			make_STEP_event: make_STEP,
			make_DMX_event: new_event_type == ADD_EVENT_BUTTON_STATES.DMX,
		};
		const { index, updated_events } = addNewEventToTable(new_event_object); //event time, is blank event, is step event, is dmx event
		index_new_event_added_to = index;
		events = updated_events; //event time, is blank event, is step event, is dmx event
	} else {
		const new_event_object = {
			show_type,
			current_wave_time,
			events,
			selected_rows,
			time: event_time,
			leave_time_blank: false,
			make_STEP_event: make_STEP,
		};
		const { index, updated_events } = addNewEventToTable(new_event_object); //event time, is blank event, is step event, is dmx event
		index_new_event_added_to = index;
		events = updated_events; //event time, is blank event, is step event, is dmx event
	}

	let new_event = events.splice(index_new_event_added_to, 1)[0];
	console.log('new_event now');

	console.log('insert below');
	if (index_to_be_below < 0 || new_event == undefined || new_event == null) {
		return;
	}

	new_event.is_inserted_event = true;
	new_event.timeline_event_time = dropped_to_waveform_event_time ?? events[index_to_be_below - 1].timeline_event_time; //Removed to simply; In some shows it may be a little more inefficient 7/9/2020
	console.log('new_event timeline_event_time');

	events.splice(index_to_be_below, 0, new_event);

	return { events, added_event_id: new_event.event_id };
}

export function addNewEventToTable({ show_type, current_wave_time, events, selected_rows, time = undefined, leave_time_blank = false, make_STEP_event = false, make_DMX_event = false, number_of_events_to_add = 1, make_ALTERNATE1 = false, make_ALTERNATE2 = false }) {
	// let add_cue_start_time = new Date();
	if (useAuthStore().active_products.includes(PRODUCTS.CSC) == false && useAuthStore().active_products.includes(PRODUCTS.CSC_LEGACY) == false) {
		if (events.length >= MAX_NUMBER_OF_FREE_TIER_EVENTS) {
			NotificationCenter.add(new NotificationHandler.Notification('Free Tier Limit', `You have reached the maximum number of events allowed on your trial plan.`, [], NotificationType.ERROR, { has_close_button: true }));
			return;
		}
	}

	if (make_ALTERNATE1 == true) {
		const alternate_event_length = events.filter((event) => event.isALTERNATE1 == true).length;
		if (alternate_event_length >= 18) {
			NotificationCenter.add(new NotificationHandler.Notification('Max Alternate 1 Events', `You have reached the maximum number of alternate 1 events allowed.`, [], NotificationType.ERROR, { has_close_button: true }));
			return;
		}
	}

	if (make_ALTERNATE2 == true) {
		const alternate_event_length = events.filter((event) => event.isALTERNATE2 == true).length;
		if (alternate_event_length >= 18) {
			NotificationCenter.add(new NotificationHandler.Notification('Max Alternate 2 Events', `You have reached the maximum number of alternate 2 events allowed.`, [], NotificationType.ERROR, { has_close_button: true }));
			return;
		}
	}

	if (show_type == SHOW_TYPES.DMX.key && make_STEP_event == false) {
		make_DMX_event = true;
	} //Only DMX show - only insert DMX & STEP events
	if (show_type == SHOW_TYPES.PYRO.key && make_STEP_event == false) {
		make_DMX_event = false;
	} //Only PYRO show - only insert PYRO & STEP events

	if (number_of_events_to_add == undefined || number_of_events_to_add <= 0) {
		number_of_events_to_add = 1;
	}

	let event_time_in_seconds = 0;
	let event_time = '';
	let timeline_time = 0;

	if (leave_time_blank) {
		event_time_in_seconds = undefined;
		event_time = undefined;
		timeline_time = undefined;
		console.log('[ADD CUE] Current Time if above:', event_time, event_time_in_seconds);
	} else {
		if (time == undefined) {
			//Set to current time of waveform
			//Limit two 2 decimal places to prevent any Javascript rounding errors like 511.960000000000000004;
			event_time_in_seconds = getDecimalPlaces(current_wave_time, 2);
			event_time = formatDisplayableTime(event_time_in_seconds);
			timeline_time = event_time_in_seconds;
		} else {
			//Limit two 2 decimal places to prevent any Javascript rounding errors like 511.960000000000000004;
			event_time_in_seconds = getDecimalPlaces(time, 2);
			event_time = formatDisplayableTime(event_time_in_seconds);
			timeline_time = event_time_in_seconds;
		}
	}

	let pre_fire_time = 0;
	let universe = undefined;
	let settings_store = useSettingStore();

	if (make_DMX_event == true) {
		if (settings_store.settings?.should_auto_fill_DMX_pre_fire == true) {
			pre_fire_time = settings_store?.settings?.default_DMX_pre_fire_value ?? 0;
		}
		if (settings_store.settings?.default_dmx_universe != undefined && settings_store.settings?.default_dmx_universe != -1) {
			universe = settings_store.settings?.default_dmx_universe ?? 0;
		}
	} else {
		if (settings_store?.settings?.should_auto_fill_prefire == true) {
			pre_fire_time = settings_store?.settings?.default_pre_fire_time ?? 0;
		}
	}

	let index = getIndexOfEventToBeAdded(timeline_time, events, selected_rows) ?? 0;
	let new_event_id = createUniqueID();
	let event_data = {
		'event_id': new_event_id,
		'index': index,
		'event_time': event_time,
		'event_time_in_seconds': event_time_in_seconds,
		'timeline_event_time': timeline_time,
		'isSTEP': false,
		'isALTERNATE1': !!make_ALTERNATE1,
		'isALTERNATE2': !!make_ALTERNATE2,
		'isDMX': make_DMX_event,
		'cue': undefined,
		'firework_name': undefined,
		'quantity': undefined,
		'duration': undefined,
		'duration_in_seconds': undefined,
		'igniter_pre_fire': shortenDisplayTime(formatDisplayableTime(pre_fire_time)),
		'igniter_pre_fire_in_seconds': pre_fire_time,
		'lift_time': '0',
		'lift_time_in_seconds': 0,
		'wikifirework_ID': undefined,
		'local_my_fireworks_ID': '',
		'universe': universe,
		'parent_event': null,
		'warnings': [
			{
				'reason': undefined,
				'column': undefined,
			},
		],
		'errors': [
			{
				'reason': undefined,
				'column': undefined,
			},
		],
	};

	let index_new_event_added_to = index;

	const last_selected_row_index = events.findIndex((event) => event.event_id == selected_rows[selected_rows.length - 1]);
	const last_selected_event = events[last_selected_row_index];
	// if a step event is selected and the current waveform time is greater than or equal to the last selected event time
	if (last_selected_event != undefined && last_selected_event.isSTEP == true && event_data.timeline_event_time >= last_selected_event.timeline_event_time && event_data.isSTEP != true) {
		event_data.parent_event = last_selected_event.event_id;
		for (let i = last_selected_row_index + 1; i < events.length; i++) {
			const event = events[i];
			if (event.isALTERNATE1 == true || event.isALTERNATE2 == true || event.isALTERNATE == true || event.isSTEP == true) {
				index_new_event_added_to = i;
				break;
			} else {
				if (event.timeline_event_time > event_data.timeline_event_time) {
					index_new_event_added_to = i;
				} else {
					continue;
				}
			}
		}
	} else {
		let parent_step = undefined;
		for (let i = index_new_event_added_to - 1; i >= 0; i--) {
			const event = events[i];
			if (event.isSTEP == true) {
				parent_step = event;
				break;
			}
		}

		if (parent_step != undefined) {
			event_data.parent_event = parent_step.event_id;
		}
	}

	events.splice(index_new_event_added_to, 0, event_data);

	let inserted_events = [event_data];

	if (number_of_events_to_add > 1) {
		for (let i = 1; i < number_of_events_to_add; i++) {
			let index = index_new_event_added_to + i;
			let new_event = JSON.parse(JSON.stringify(event_data));
			new_event.event_id = createUniqueID();
			new_event.index = index;

			events.splice(index, 0, new_event);
			inserted_events.push(new_event);
		}
	}

	// Improvement: check if this needs to replicated
	// cueHandler.recalculateTimelineEventTime();

	if (make_STEP_event) {
		// as per new requirement i.e evs-318 : the newly added step should always be snapped/attached to the previous timed or step event
		// we can use filter as at this point all the events should always be sorted w.r.t timeline_event_time
		let { previous_event_time, previous_event_time_in_seconds, previous_event_duration_in_seconds } = getPreviousEventDetails(events, index_new_event_added_to);
		if (previous_event_duration_in_seconds > 0) {
			previous_event_time_in_seconds = previous_event_time_in_seconds + previous_event_duration_in_seconds;
			previous_event_time = formatDisplayableTime(previous_event_time_in_seconds);
		}

		for (let i = 0; i < number_of_events_to_add; i++) {
			let step_index = index_new_event_added_to + i;
			events[step_index].isSTEP = true;
			events[step_index].timeline_event_time = previous_event_time_in_seconds;
			events[step_index].event_time_in_seconds = previous_event_time_in_seconds;
			events[step_index].event_time = previous_event_time;
			// events = updateTimelineEventTime(events); //I don't think this was implemented right. It seems like it is designed to fix a specific bug but it is called every time a STEP is added.
		}

		createStepParentChildRelationship({ start_from_row_index: index_new_event_added_to });
	}

	useShowStore().setStepEventsList();

	updateOffsetsAndRelativeTime({ updated_event: events[index_new_event_added_to] });

	return { index: index_new_event_added_to, updated_events: events, new_event: event_data };
}

// function updateTimelineEventTime(events) {
// 	if (events == undefined) {
// 		return;
// 	}

// 	let event_index = -1;
// 	let use_absolute_STEP_time = useSettingStore()?.settings.use_absolute_STEP_time || false;

// 	for (let event of events) {
// 		event_index++;

// 		if (event.isALTERNATE1 || event.isALTERNATE2) {
// 			break;
// 		}
// 		if (event.isSTEP) {
// 			if (event_index - 1 < 0) {
// 				//First cue
// 				console.warn('[TIMELINE EVENT TIME] Timeline Event Time Changed');
// 				if (use_absolute_STEP_time) {
// 					console.log("Didn't change timleine_event_time");
// 				} else {
// 					events[event_index].timeline_event_time = 0.0;
// 					events[event_index].event_time_in_seconds = 0.0;
// 					events[event_index].event_time = formatDisplayableTime(0.0);
// 				}
// 			} else {
// 				if (use_absolute_STEP_time) {
// 					console.log("Didn't change timleine_event_time");
// 				} else {
// 					const new_time = events[event_index - 1].timeline_event_time + (events[event_index - 1].duration_in_seconds || 0);
// 					events[event_index].timeline_event_time = new_time;
// 					events[event_index].event_time_in_seconds = new_time;
// 					events[event_index].event_time = formatDisplayableTime(new_time);
// 				}
// 			}
// 		} else {
// 			events[event_index].timeline_event_time = event.event_time_in_seconds;
// 		}
// 	}
// 	return events;
// }

export function validateShow(show) {
	let index_for_event_id = {};

	//Improvement: this needs to be a global variable
	let used_events_ids = [];

	if (customColumnsContainReturnCharacterCode(show.custom_columns) == true) {
		//See if it needs to fix issue with importing shows with custom columns 3/32/2020
		show = fixImportedCustomColumnBug(show);
	} else {
		console.log('[Fix Custom Column Import] Either not imported or already fixed.');
	}

	show = fixStringChannelValues(show); //Retroactily fix shows that were affected by a bug causing the channel to be stored as a string rather than an int 4/18/2020

	//Handle the Custom Columns
	// cueHandler.validateCustomColumns(script);

	if (show.events != undefined && show.events.length >= 1) {
		//Get the show type
		let show_has_STEP_events = !!show.events.find((event) => event.isSTEP == true);
		let show_has_timed_events = !!show.events.find((event) => event.isSTEP != true);

		if (show_has_STEP_events == true && show_has_timed_events == true) {
			show.show_type = SHOW_TYPES_BY_EVENTS.COMPOUND;
		}
		if (show_has_STEP_events == true && show_has_timed_events == false) {
			show.show_type = SHOW_TYPES_BY_EVENTS.STEP;
		}
		if (show_has_STEP_events == false && show_has_timed_events == true) {
			show.show_type = SHOW_TYPES_BY_EVENTS.TIMED;
		}
	} else {
		show.show_type = SHOW_TYPES_BY_EVENTS.BLANK;
	}

	// Sort the events
	show.events = sortEvents(show.events);

	let should_try_to_fix_timeline_event_time_bug = false;

	// Check the events
	for (let index = 0; index < show.events.length; index++) {
		let event = show.events[index];

		if (event.event_time_in_seconds < 0 || event.timeline_event_time < 0) {
			//Check if this has been affected by the timeline_event_time bug
			console.error('[Timeline Event Time Bug] Found negative values! ', event);
			should_try_to_fix_timeline_event_time_bug = true;

			// TODO: uncomment after adding google analytics
			// ga('send', 'event', 'timeline_event_bug', 'found_negative_value', 'bug_fix', parseInt(show.show_ID.toBinary()));
		}

		//Check if event has a event ID; if not create one;
		if (event.event_id == undefined || event.event_id == null || event.event_id === '') {
			let event_id = createUniqueID();
			show.events[index].event_id = event_id;
			event.event_id = event_id;
		}

		used_events_ids.push(event.event_id); //Maybe rename to used_event_ids
		index_for_event_id[event.event_id] = index;

		// sanitize the custom_values by removing end spaces and special characters
		console.log('check custom_values before sanitization', event.custom_values);
		if (event.custom_values != undefined) {
			event.custom_values = Object.fromEntries(Object.entries(event.custom_values).map(([key, value]) => [customColumnSanitize(key), value]));
		}
		console.log('check custom_values after sanitization', event.custom_values);
	}

	// sanitize the custom_columns by removing end spaces and special characters
	console.log('check custom_columns before sanitization', show.custom_columns);
	if (show.custom_columns != undefined) {
		show.custom_columns = show.custom_columns.map((column) => customColumnSanitize(column));
	}
	console.log('check custom_columns after sanitization', show.custom_columns);

	show.events = validateEvents(show); //Don't redraw the cueTable

	//Fix bug that was introducated to producation and beta branch with the updateTimelineEventTime();
	if (show.fixed_timeline_event_time_bug_0_2_70 == undefined || show.fixed_timeline_event_time_bug_0_2_70 == false || should_try_to_fix_timeline_event_time_bug == true) {
		console.log('[Cue Table] [Timeline Event Time Bug] Trying to fix timeline_event_time bug from Nov. 13th 2019...');
		if (show.manually_fixed_timeline_event_time_0_2_70 == undefined || show.manually_fixed_timeline_event_time_0_2_70 == false) {
			// Improvement: implement this function
			// fixTimelineEventTimeBug();
			console.log('Call timeline bug fix');
		}
	} else {
		console.log('[Cue Table] [Timeline Event Time Bug] timeline_event_time bug from Nov. 13th 2019 was marked as fixed. (manually fixed: ', show.manually_fixed_timeline_event_time_0_2_70, ')');
	}

	// Improvement: Check use
	// tryToMatchFireworksToMyFireworks(); //Added to help try and match previously imported shows cues to their My Firework ids 3/24/2020

	return show;
}

function customColumnsContainReturnCharacterCode(columns) {
	if (columns == undefined || null) {
		return false;
	}
	for (const column of columns) {
		if (column.charCodeAt(column.length - 1) == 13) {
			return true;
		}
	}
	return false;
}

function fixImportedCustomColumnBug(show) {
	//Fixing a bug that was caused by importing custom columns; Custom columns had an invisable character (13 enter) at the end of the name causing issues. 3/23/2020
	console.log('Fixing imported custom columns...');

	let old_custom_columns = JSON.parse(JSON.stringify(show.custom_columns));
	let columns = show.custom_columns;

	for (let i = 0; i < columns.length; i++) {
		columns[i] = columns[i].replace(/[\n\r]/g, '');
	}

	show.custom_columns = columns;

	for (let i = 0; i < show.events.length; i++) {
		let event = show.events[i];

		for (let column_index = 0; column_index < old_custom_columns.length; column_index++) {
			if (event.custom_values != undefined && columns[column_index] !== old_custom_columns[column_index]) {
				show.events[i].custom_values[columns[column_index]] = event.custom_values[old_custom_columns[column_index]];

				if (show.events[i].custom_values[old_custom_columns[column_index]] == show.events[i].custom_values[columns[column_index]]) {
					delete show.events[i].custom_values[old_custom_columns[column_index]];
				}
			}
		}
	}
	return show;
}

function fixStringChannelValues(show) {
	//Retroactily fix shows that were affected by a bug causing the channel to be stored as a string rather than an int 4/18/2020
	console.log('Fixing Channel Strings Bug...');

	if (show == undefined || show.events == undefined) {
		console.log('[Fix Channel Strings Bug] No script object; No need to fix', show);
		return show;
	}

	let update_released_on = new Date('5/18/2020');
	if (new Date(show.created_date) > update_released_on) {
		console.log('[Fix Channel Strings Bug] Show was created after update was released; No need to fix.');
		return show;
	}

	let last_edit_date = new Date('6/1/2020');
	if (new Date(show.last_edited) > last_edit_date) {
		console.log('[Fix Channel Strings Bug] Show was last edited after ' + last_edit_date + '; The show should have already been fixed; No need to fix.');
		return show;
	}

	let has_string_channel = false;
	for (let i = 0; i < show.events.length; i++) {
		if (typeof show.events[i].channel == 'string') {
			has_string_channel = true;
			break;
		}
	}
	if (has_string_channel == false) {
		console.log('[Fix Channel Strings Bug] All channels already are ints; No need to fix.');
		return show;
	}

	console.log('[Fix Channel Strings Bug] Trying to convert string channels to ints...'); //All checks are complete; Start converting channels

	for (let i = 0; i < show.events.length; i++) {
		if (typeof show.events[i].channel == 'string') {
			let channel_int = parseInt(show.events[i].channel);
			if (isNaN(channel_int) == false) {
				show.events[i].channel = channel_int;
			}
		}
	}

	return show;
}

export function validateEvent(show, event, event_array_index, previous_cue, previous_channel, channel_added_to_new_event = false, previous_cue_before_auto_increment = undefined, cue_added_to_blank_cue = false) {
	if (event_array_index == undefined) {
		event_array_index = show.events.findIndex((active_show_event) => active_show_event.event_id == event.event_id);
	}

	removeErrorsAndWarningsFromEvent(event_array_index, show.events);

	// TODO: implement later after use case clarification.
	// await checkForRefiredCues()

	if (previous_cue != undefined || cue_added_to_blank_cue === true) {
		updateUsedCuesAfterEventCueChange(event, show, previous_cue);
	}

	if (previous_channel != undefined || channel_added_to_new_event === true) {
		updateUsedCuesAfterEventChannelChange(event, show, previous_channel, previous_cue_before_auto_increment);
	}
	console.log('check used cues', used_cues);

	validateEventElement(event, show);
}

function updateUsedCuesAfterEventCueChange(event, show, previous_cue) {
	if (used_cues[event.channel] == undefined) {
		return;
	}

	if (used_cues[event.channel][previous_cue] != undefined) {
		if (used_cues[event.channel][previous_cue]?.indexOf(event.event_id) >= 0) {
			used_cues[event.channel][previous_cue].splice(used_cues[event.channel][previous_cue].indexOf(event.event_id), 1);
		}
	}

	let unique_cue_event_in_channel_for_new_cue_value = undefined;
	if (used_cues[event.channel][event.cue]?.length == 1) {
		const event_id_of_unique_cue_event_in_channel_for_new_cue_value = used_cues[event.channel][event.cue][0];
		unique_cue_event_in_channel_for_new_cue_value = show.events.find((show_event) => event_id_of_unique_cue_event_in_channel_for_new_cue_value == show_event.event_id);
	}

	if (used_cues[event.channel][event.cue] != undefined) {
		if (!used_cues[event.channel][event.cue].includes(event.event_id)) {
			used_cues[event.channel][event.cue].push(event.event_id);
		}
	} else {
		used_cues[event.channel][event.cue] = [event.event_id];
	}
	calculateNumberOfCuesUsedForChannel(show.firmware_version);
	checkForRefiredEvents(event, show.firmware_version);

	if (unique_cue_event_in_channel_for_new_cue_value != undefined) {
		calculateNumberOfCuesUsedForChannel(show.firmware_version);
		checkForRefiredEvents(unique_cue_event_in_channel_for_new_cue_value, show.firmware_version);
	}

	if (used_cues[event.channel][previous_cue]?.length == 1) {
		const event_id_of_unique_event_left_in_channel_for_previous_cue = used_cues[event.channel][previous_cue][0];
		const unique_cue_event_in_channel_for_previous_cue_value = show.events.find((show_event) => event_id_of_unique_event_left_in_channel_for_previous_cue == show_event.event_id);
		if (unique_cue_event_in_channel_for_previous_cue_value != undefined) {
			calculateNumberOfCuesUsedForChannel(show.firmware_version);
			checkForRefiredEvents(unique_cue_event_in_channel_for_previous_cue_value, show.firmware_version);
		}
	}
}

function updateUsedCuesAfterEventChannelChange(event, show, previous_channel, previous_cue_before_auto_increment) {
	if (event.channel === '' || event.channel == undefined) {
		return;
	}

	if (previous_cue_before_auto_increment != undefined) {
		if (used_cues[previous_channel] != undefined && used_cues[previous_channel][previous_cue_before_auto_increment] != undefined) {
			console.log('check used this value 1', used_cues[previous_channel][previous_cue_before_auto_increment].indexOf(event.event_id));

			if (used_cues[previous_channel][previous_cue_before_auto_increment]?.indexOf(event.event_id) >= 0) {
				used_cues[previous_channel][previous_cue_before_auto_increment].splice(used_cues[previous_channel][previous_cue_before_auto_increment].indexOf(event.event_id), 1);
			}
		}
	}

	if (used_cues[previous_channel] != undefined && used_cues[previous_channel][event.cue] != undefined) {
		console.log('check used this value', used_cues[previous_channel][event.cue].indexOf(event.event_id));

		if (used_cues[previous_channel][event.cue].indexOf(event.event_id) >= 0) {
			used_cues[previous_channel][event.cue].splice(used_cues[previous_channel][event.cue].indexOf(event.event_id), 1);
		}
	}

	let unique_cue_event_in_channel_for_new_channel_value = undefined;
	if (used_cues[event.channel] != undefined && used_cues[event.channel][event.cue]?.length == 1) {
		const event_id_of_unique_cue_event_in_channel_for_new_channel_value = used_cues[event.channel][event.cue][0];
		unique_cue_event_in_channel_for_new_channel_value = show.events.find((show_event) => event_id_of_unique_cue_event_in_channel_for_new_channel_value == show_event.event_id);
	}

	if (used_cues[event.channel] == undefined) {
		used_cues[event.channel] = {};
	}

	// No need to proceed if channel is just newly attached to event and has no cue
	if (event.cue == undefined || event.cue === '') {
		return;
	}

	if (used_cues[event.channel][event.cue] != undefined) {
		if (!used_cues[event.channel][event.cue].includes(event.event_id)) {
			used_cues[event.channel][event.cue].push(event.event_id);
		}
	} else {
		used_cues[event.channel][event.cue] = [event.event_id];
	}
	calculateNumberOfCuesUsedForChannel(show.firmware_version);
	checkForRefiredEvents(event, show.firmware_version);

	if (unique_cue_event_in_channel_for_new_channel_value != undefined) {
		calculateNumberOfCuesUsedForChannel(show.firmware_version);
		checkForRefiredEvents(unique_cue_event_in_channel_for_new_channel_value, show.firmware_version);
	}

	if (used_cues[previous_channel] != undefined && used_cues[previous_channel][event.cue]?.length == 1) {
		const event_id_of_unique_event_left_in_channel_for_previous_channel = used_cues[previous_channel][event.cue][0];
		const unique_cue_event_in_channel_for_previous_channel_value = show.events.find((show_event) => event_id_of_unique_event_left_in_channel_for_previous_channel == show_event.event_id);
		if (unique_cue_event_in_channel_for_previous_channel_value != undefined) {
			calculateNumberOfCuesUsedForChannel(show.firmware_version);
			checkForRefiredEvents(unique_cue_event_in_channel_for_previous_channel_value, show.firmware_version);
		}
	}
}

export function validateEvents(show) {
	// Improvement: check if used_cues needs to be global.

	// reset used cues related values
	resetValuesUsedForChannelCueCalculations(show.firmware_version);

	let settings_store = useSettingStore();

	// TODO: check use
	// locateAllUsedCues();

	for (let i = 0; i < show.events.length; i++) {
		removeErrorsAndWarningsFromEvent(i, show.events);
	}

	show.events = calculateEventTimes(show.events, show.firmware_version);

	for (let i = 0; i < show.events.length; i++) {
		let event = show.events[i];
		const adding_cue_to_used_cue_result = addCueToUsedCues(event.channel, event.cue, event.event_id);
		used_cues = adding_cue_to_used_cue_result.used_cues;
		channel_and_cues = adding_cue_to_used_cue_result.channel_and_cues;
	}

	// Specific function for ignite shows
	if (show.equipment_manufacture === SHOW_EQUIPMENT_MANUFACTURES.IGNITE.key && settings_store?.settings?.ignite_settings?.using_clip_on_igniters == true) {
		for (const channel in used_cues) {
			let events_array = show.events.filter((event) => event.channel == channel);
			events_array = events_array.sort((a, b) => a.calculated_event_time_in_seconds - b.calculated_event_time_in_seconds);
			for (let i = 0; i < events_array.length; i++) {
				if (i + 1 >= events_array.length) {
					break;
				}
				if (Math.abs(events_array[i].calculated_event_time_in_seconds - events_array[i + 1].calculated_event_time_in_seconds) < 2) {
					const index_for_event_ID = returnIndexOfEventIds(show.events);

					show.events = addIssueToArray(
						'errors',
						index_for_event_ID[events_array[i].event_id],
						`Make sure you have at least two seconds between events on the same module. For example, if you fire a cue on a module at 10 seconds, make sure to have the next event fire no earlier than 12 seconds. This allows for a full two seconds between cues.\n\nIf you are using professional e-match or MJG igniters. you can disable this warning from the Preferences section.`,
						1,
						'within_2_seconds',
						show.events
					);
					show.events = addIssueToArray(
						'errors',
						index_for_event_ID[events_array[i + 1].event_id],
						`Make sure you have at least two seconds between events on the same module. For example, if you fire a cue on a module at 10 seconds, make sure to have the next event fire no earlier than 12 seconds. This allows for a full two seconds between cues.\n\nIf you are using professional e-match or MJG igniters. you can disable this warning from the Preferences section.`,
						1,
						'within_2_seconds',
						show.events
					);
				}
			}
		}
	}

	calculateNumberOfCuesUsedForChannel(show.firmware_version);

	for (let i = 0; i < show.events.length; i++) {
		let event = show.events[i];
		checkForRefiredEvents(event, show.firmware_version);
		validateEventElement(event, show);
	}
	return show.events;
}

// Detects existing addresses where 2+ distinct positions already share the same channel, so Auto Address
// can warn the user and exclude that channel entirely instead of one position silently absorbing the other's channel.
export function findAutoAddressChannelConflicts(events, events_to_address_ids, existing_addresses_mode) {
	const channel_positions = new Map(); // channel (string) -> Map<position_key, position_label>
	const channel_has_in_scope_event = new Map(); // channel (string) -> true if any contributing event is in scope

	for (const show_event of events ?? []) {
		if (show_event.isDMX == true || show_event.isCOMMENT == true) {
			continue;
		}

		const has_channel = show_event.channel != undefined && show_event.channel !== '';
		const has_cue = show_event.cue != undefined && show_event.cue !== '';
		if (has_channel == false || has_cue == false) {
			continue;
		}

		const is_in_scope = events_to_address_ids.has(show_event.event_id);
		// An event is "frozen" (won't be reassigned this run) if it's locked, out of scope, or fill_blanks mode
		// is leaving an already-fully-addressed in-scope event untouched.
		const is_frozen = show_event.is_address_locked == true || is_in_scope == false || (existing_addresses_mode === 'fill_blanks' && is_in_scope == true);
		if (is_frozen == false) {
			continue;
		}

		const channel = String(show_event.channel);
		const position_key = show_event.defined_position?.id ?? '__no_position__';
		const position_label = show_event.defined_position?.label ?? null;

		if (channel_positions.has(channel) == false) {
			channel_positions.set(channel, new Map());
		}
		channel_positions.get(channel).set(position_key, position_label);

		if (is_in_scope == true) {
			channel_has_in_scope_event.set(channel, true);
		}
	}

	const blocked_channels = new Set();
	const in_scope_conflicts = [];
	const out_of_scope_conflicts = [];

	for (const [channel, positions] of channel_positions) {
		if (positions.size < 2) {
			continue;
		}
		blocked_channels.add(channel);
		// Keep null entries (events with no defined_position), the caller is responsible for giving them a display label.
		const position_labels = [...positions.values()];
		const conflict_record = { channel, position_labels };
		if (channel_has_in_scope_event.get(channel) == true) {
			in_scope_conflicts.push(conflict_record);
		} else {
			out_of_scope_conflicts.push(conflict_record);
		}
	}

	return { blocked_channels, in_scope_conflicts, out_of_scope_conflicts };
}

// Groups the errors/warnings (added to events by validateEvents) by issue type and severity for the export errors modal.
export function groupEventIssuesForExport(events) {
	const groups_by_key = new Map();
	let has_channel_or_cue_empty = false;

	for (let event_index = 0; event_index < events.length; event_index++) {
		const event = events[event_index];
		const row_number = event_index + 1;
		const event_item = {
			row_number,
			event_time: event.event_time,
			description: event.isCOMMENT == true ? event.comment : event.firework_name,
			is_dmx: event.isDMX !== false && event.isDMX != undefined && event.isCOMMENT == undefined,
		};

		for (const severity_object of [
			{ severity: 'error', issues: event.errors },
			{ severity: 'warning', issues: event.warnings },
		]) {
			for (const issue of severity_object.issues ?? []) {
				if (severity_object.severity == 'error' && (issue.type == 'channel_empty' || issue.type == 'cue_empty') && event.isDMX != true) {
					has_channel_or_cue_empty = true;
				}

				const group_key = `${severity_object.severity}_${issue.type}`;
				if (groups_by_key.has(group_key) == false) {
					groups_by_key.set(group_key, {
						type: issue.type,
						severity: severity_object.severity,
						label_key: EVENT_ISSUE_TYPE_LABELS[issue.type] ?? EVENT_ISSUE_TYPE_LABEL_FALLBACK,
						items: [],
					});
				}

				groups_by_key.get(group_key).items.push(event_item);
			}
		}
	}

	const groups = Array.from(groups_by_key.values());
	groups.sort((group_a, group_b) => {
		if (group_a.severity == group_b.severity) {
			return 0;
		}

		return group_a.severity == 'error' ? -1 : 1;
	});

	return { groups, has_channel_or_cue_empty };
}

function calculateNumberOfCuesUsedForChannel(firmware_version) {
	if (firmware_version != undefined && firmware_version >= 6.0) {
		for (let channel in used_cues) {
			number_of_cues_used_for_channel[channel] = 0;
			for (let cue_number in used_cues[channel]) {
				number_of_cues_used_for_channel[channel] += used_cues[channel][cue_number].length;
			} //Get how many cues are used for this channel
		}
	}
}

function checkForRefiredEvents(event, firmware_version) {
	// Removed: was silently overwriting event.cue with parseInt result, causing semicolon/NaN errors to never show
	// let force_int_cue = parseInt(event.cue);
	// if (isNaN(force_int_cue) == false) {
	// 	event.cue = force_int_cue;
	// }

	if (event.isCOMMENT == true) {
		removeIssueFromEvent(event, 'errors', 'same_cue');
		removeIssueFromEvent(event, 'warnings', 'same_cue');
	} else {
		if (event.warnings.length > 0) {
			removeIssueFromEvent(event, 'warnings', 'same_cue');
		}
		if (event.errors.length > 0) {
			removeIssueFromEvent(event, 'errors', 'same_cue');
		}

		if (event.isDMX != true && firmware_version >= 6.0 && max_number_of_events_per_channel != undefined && number_of_cues_used_for_channel[event.channel] > max_number_of_events_per_channel) {
			//Check if the channel has already been refired too many times
			addIssueToEvent('errors', `This channel has exceeded the maximum number of re-fires. Max re-fires: ${max_number_of_events_per_channel}.`, EVENT_TABLE_COLUMNS.CHANNEL, 'same_cue', event);
		}

		if (event.isDMX != true && max_number_of_times_a_cue_can_be_refired != undefined && used_cues[event.channel] != undefined && used_cues[event.channel][event.cue] != undefined && used_cues[event.channel][event.cue].length > max_number_of_times_a_cue_can_be_refired) {
			console.log('used_cues :  ', used_cues);
			//Check if the cue has already been refired too many times
			addIssueToEvent('warnings', `This cue, on this channel has exceeded the maximum number of re-fires. Max re-fires: ${max_number_of_times_a_cue_can_be_refired}.`, 3, 'same_cue', event);
		}
	}
}

function removeIssueFromArray(events, objectKey = 'warnings', event_index, type) {
	if (events[event_index][objectKey].length > 0) {
		for (let i = 0; i < events[event_index][objectKey].length; i++) {
			if (events[event_index][objectKey][i].type == type) {
				events[event_index][objectKey].splice(i, 1);
			}
		}
	}

	return events;
}

function removeIssueFromEvent(event, objectKey = 'warnings', type) {
	if (event[objectKey].length > 0) {
		for (let i = 0; i < event[objectKey].length; i++) {
			if (event[objectKey][i].type == type) {
				event[objectKey].splice(i, 1);
			}
		}
	}
}

export function addCueToUsedCues(channel, cue, event_id) {
	if (channel == undefined || channel === '') {
		return { used_cues, channel_and_cues };
	}
	if (cue == undefined || cue === '') {
		return { used_cues, channel_and_cues };
	}

	let force_int_cue = parseInt(cue);

	if (isNaN(force_int_cue) == false) {
		cue = force_int_cue;
	}

	channel_and_cues.push({ 'channel': channel, 'cue': cue, 'event_id': event_id });

	if (used_cues[channel] == undefined || used_cues[channel] == null) {
		used_cues[channel] = {};
		used_cues[channel][cue] = [event_id];
	} else {
		if (used_cues[channel][cue] == undefined) {
			used_cues[channel][cue] = [event_id];
		} else {
			used_cues[channel][cue].push(event_id);
		}
	}
	return { used_cues, channel_and_cues };
}

function addIssueToArray(objectKey = 'warnings', event_index, reason, column, type, events) {
	//Remove the old error of this type if there is one
	events = removeIssueFromArray(events, objectKey, event_index, type);

	let newObject = { 'reason': reason, 'column': column, 'type': type };
	if (objectKey == 'errors' || objectKey == 'warnings') {
		if (events[event_index][objectKey] == undefined || null) {
			events[event_index][objectKey] = [newObject];
		} else {
			events[event_index][objectKey].push(newObject);
		}
	}

	return events;
}

function addIssueToEvent(objectKey = 'warnings', reason, column, type, event) {
	let newObject = { 'reason': reason, 'column': column, 'type': type };
	if (objectKey == 'errors' || objectKey == 'warnings') {
		if (event[objectKey] == undefined) {
			event[objectKey] = [newObject];
		} else {
			event[objectKey].push(newObject);
		}
	}
}

export function validateEventElement(event, show) {
	// TODO : event_id && index shall be used in error notifications.
	// Had to return due to undefined event
	if (event == undefined) {
		console.warn('Had to return due to undefined event', event);
		return show.events;
	}
	//No need to do anything yet. Prevents comments from accidentally being flagged for a invalid error
	if (event.isCOMMENT == true) {
		return show.events;
	}
	// Do not validate DMX events when show type is PYRO ONLY
	if (show.show_attributes.show_type == SHOW_TYPES.PYRO.key && event.isDMX == true) {
		return show.events;
	}
	// Do not validate PYRO events when show type is DMX ONLY
	if (show.show_attributes.show_type == SHOW_TYPES.DMX.key && event.isDMX != true) {
		return show.events;
	}

	const firmware_limits = getFirmwareLimits(show.firmware_version);
	let channel_value_rules = { min: firmware_limits.channel_min, max: firmware_limits.channel_max };
	//Check if cue is a DMX cue
	if (event.isDMX == true) {
		channel_value_rules.min = 1;
		channel_value_rules.max = 200;
	}

	//Check if the channel is below channel min or above channel max
	if (event.channel !== '' && (event.channel < channel_value_rules.min || event.channel > channel_value_rules.max) && event.isCOMMENT != true) {
		addIssueToEvent('errors', `The only ${event.isDMX == true ? 'DMX' : ''} channels available are between ${channel_value_rules.min} and ${channel_value_rules.max}.`, 2, 'not_valid_channel', event);
	}

	if (show.show_attributes.show_type != SHOW_TYPES.PYRO.key) {
		//Validate pulse value
		let pulse_value_rules = { min: 0, max: 20 };

		if (event.isDMX == true) {
			pulse_value_rules.min = 0;
			pulse_value_rules.max = 600;
		} //600 = 10 Minutes; //Check if event is a DMX event

		if (event.pulse != undefined && event.pulse !== '' && (event.pulse < pulse_value_rules.min || event.pulse > pulse_value_rules.max) && event.isCOMMENT != true) {
			//Check if the universe is below pulse min or above pulse max

			addIssueToEvent('errors', `The ${event.isDMX == true ? 'DMX' : 'PYRO'} pulse time can only be between ${pulse_value_rules.min} and ${pulse_value_rules.max} seconds.`, EVENT_TABLE_COLUMNS.PULSE, 'not_valid_pulse_value', event);
		}

		if (event.pulse != undefined && event.pulse !== '' && /^-?\d+(\.\d+)?$/.test(event.pulse) != true) {
			// Check if pulse is not a number or out of range, and not a comment
			if (event.isCOMMENT !== true) {
				addIssueToEvent('errors', `The ${event.isDMX === true ? 'DMX' : 'PYRO'} pulse time must be a valid number between ${pulse_value_rules.min} and ${pulse_value_rules.max} seconds.`, EVENT_TABLE_COLUMNS.PULSE, 'not_valid_pulse_value', event);
			}
		}
	}

	// if Pyro cue
	// else DMX cue
	if (event.isDMX == undefined || event.isDMX == false) {
		//Check if the event is below 1 or above 18
		if (event.cue !== '' && (event.cue < 1 || event.cue > MAX_PYRO_CUE)) {
			// TODO: Add Notification for error

			addIssueToEvent('errors', `Cues can only be between 1 and ${MAX_PYRO_CUE}.`, 3, 'not_valid_cue', event);
		}
	} else {
		//DMX Cue
		//Check if the cue is below 0 or above 255
		if (event.cue !== '' && (event.cue < 0 || event.cue > 255)) {
			addIssueToEvent('errors', 'DMX values can only be between 0 and 255.', 3, 'not_valid_dmx_value', event);
		}

		let settings_store = useSettingStore();
		if (settings_store.settings?.disable_dmx_ramp_feature != true && show.firmware_version >= 8.0) {
			if (event.dmx_ramp_from_value !== '' && (event.dmx_ramp_from_value < 0 || event.dmx_ramp_from_value > 255)) {
				addIssueToEvent('errors', 'DMX values can only be between 0 and 255.', 3, 'not_valid_dmx_value', event);
			}
		}

		//Check if the universe is below 1 or above 99
		if (event.universe !== '' && (event.universe < 1 || event.universe > 99)) {
			addIssueToEvent('errors', `The only DMX Universes available are between 1 and 99.`, EVENT_TABLE_COLUMNS.UNIVERSE, 'not_valid_universe', event);
		}

		//Check if the user left a box empty
		if (event.universe == undefined || event.universe === '') {
			console.log(' check passed:');

			addIssueToEvent('errors', "You may have forgot to fill the universe field in. You don't want to forget that!", EVENT_TABLE_COLUMNS.UNIVERSE, 'universe_empty', event);
		}

		//Check if there is a 'Not A Number' value
		if (isNaN(event.universe) && event.universe != '' && event.universe != undefined) {
			// TODO: Add Notification for error
			addIssueToEvent('errors', 'There is a non-number character in the universe field', EVENT_TABLE_COLUMNS.UNIVERSE, 'universe_NaN', event);
		}
	}

	if ((event.channel == undefined || event.channel === '') && (event.isCOMMENT != true || event.isCOMMENT == undefined)) {
		//Check if the user left a box empty
		addIssueToEvent('errors', "You may have forgot to fill the channel field in. You don't want to forget that!", 2, 'channel_empty', event);
	}

	if ((event.cue == undefined || event.cue === '') && event.isCOMMENT != true) {
		// TODO: Add Notification for error
		addIssueToEvent('errors', 'You may have forgot to fill the cue field in. That would be bad!', 3, 'cue_empty', event);
	}

	//Check if there is a 'Not A Number' value

	if (isNaN(event.channel) && event.channel !== '' && event.channel != undefined && event.isCOMMENT != true) {
		addIssueToEvent('errors', 'There is a non-number character in the channel field.', 2, 'channel_NaN', event);
	}
	if (isNaN(event.cue) && event.cue != '' && event.cue != undefined && event.isCOMMENT != true) {
		// TODO: Add Notification for error
		addIssueToEvent('errors', 'There is a non-number character in the cue field. When did we create a module with that many cues?', 3, 'cue_NaN', event);
	}
	if (event.duration !== '' && event.duration != undefined && isNaN(event.duration.replace(/s/g, '')) && event.isCOMMENT != true) {
		addIssueToEvent('errors', 'There is a non-number character in the duration field.', 5, 'duration_NaN', event);
	}
	if (event.igniter_pre_fire !== '' && event.igniter_pre_fire != undefined && isNaN(event.igniter_pre_fire.replace(/s/g, '')) && event.isCOMMENT != true) {
		addIssueToEvent('errors', 'There is a non-number character in the pre-fire field.', 6, 'pre_fire_NaN', event);
	}
	if (event.lift_time !== '' && event.lift_time != undefined && isNaN(event.lift_time.replace(/s/g, '')) && event.isCOMMENT != true) {
		addIssueToEvent('errors', 'There is a non-number character in the lift time field.', 7, 'lift_time_NaN', event);
	}
	//Check if the calculated event time is below 0 (negative);
	if ((event.negative_calculated_event_time != undefined || null) && event.negative_calculated_event_time == true && event.isCOMMENT != true) {
		addIssueToEvent('warnings', 'Due to lift time and pre-fire time being subtracted the earliest we can fire this cue is at 0:00.00 which may be to late.', 1, 'negative_calcualted_event_time', event);
	}
	//Check if there is a 'Not A Number' value
	if (show.show_attributes.show_type != SHOW_TYPES.PYRO.key) {
		if (event.pulse !== '' && event.pulse != undefined && isNaN(event.pulse.toString().replace(/s/g, '')) && event.isCOMMENT != true) {
			addIssueToEvent('errors', 'There is a non-number character in the pulse field.', EVENT_TABLE_COLUMNS.PULSE, 'pulse_NaN', event);
		}
	}
	//Check for a comma

	// if (event.isCOMMENT == true) {
	// 	if (event.comment != undefined && ('' + event.comment).indexOf(',') > -1) {
	// 		 addIssueToEvent('errors',  'You can not use commas!', 9, 'comma', event);
	// 	}
	// } else {
	// 	if (event.universe != undefined && ('' + event.universe).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', EVENT_TABLE_COLUMNS.UNIVERSE, 'comma', event);
	// 	}
	// 	if (event.channel != undefined && ('' + event.channel).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', 2, 'comma', event);
	// 	}
	// 	if (event.cue != undefined && ('' + event.cue).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', 3, 'comma', event);
	// 	}
	// 	if (event.firework_name != undefined && ('' + event.firework_name).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', 4, 'comma', event);
	// 	}
	// 	if (event.duration != undefined && ('' + event.duration).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', 5, 'comma', event);
	// 	}
	// 	if (event.igniter_pre_fire != undefined && ('' + event.igniter_pre_fire).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', 6, 'comma', event);
	// 	}
	// 	if (event.lift_time != undefined && ('' + event.lift_time).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', 7, 'comma', event);
	// 	}
	// 	if (event.pulse != undefined && ('' + event.pulse).indexOf(',') > -1) {
	// 		// TODO: Add Notification for error
	// 		 addIssueToEvent('errors',  'You can not use commas!', EVENT_TABLE_COLUMNS.PULSE, 'comma', event);
	// 	}
	// }

	//Check for a semicolon
	if (event.isCOMMENT == true) {
		if (event.comment != undefined && ('' + event.comment).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 9, 'semicolon', event);
		}
	} else {
		if (event.universe != undefined && ('' + event.universe).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', EVENT_TABLE_COLUMNS.UNIVERSE, 'semicolon', event);
		}
		if (event.channel != undefined && ('' + event.channel).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 2, 'semicolon', event);
		}
		if (event.cue != undefined && ('' + event.cue).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 3, 'semicolon', event);
		}
		if (event.firework_name != undefined && ('' + event.firework_name).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 4, 'semicolon', event);
		}
		if (event.duration != undefined && ('' + event.duration).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 5, 'semicolon', event);
		}
		if (event.igniter_pre_fire != undefined && ('' + event.igniter_pre_fire).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 6, 'semicolon', event);
		}
		if (event.lift_time != undefined && ('' + event.lift_time).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', 7, 'semicolon', event);
		}
		if (event.pulse != undefined && ('' + event.pulse).indexOf(';') > -1) {
			addIssueToEvent('errors', 'You can not use semicolons!', EVENT_TABLE_COLUMNS.PULSE, 'semicolon', event);
		}
		if (event.custom_values != undefined) {
			for (const [custom_column_key, custom_column_value] of Object.entries(event.custom_values)) {
				if (custom_column_value != undefined && ('' + custom_column_value).indexOf(';') > -1) {
					addIssueToEvent('errors', 'You can not use semicolons!', custom_column_key, 'semicolon', event);
				}
			}
		}
	}
}

export function removeErrorsAndWarningsFromEvent(event_index, events) {
	events[event_index].errors = [];
	events[event_index].warnings = [];
}

export function sortByCalculatedTime(a, b) {
	if (a['isSTEP'] && !b['isALTERNATE1'] && !b['isALTERNATE2']) {
		console.log('A is STEP');
		return 0;
	}
	if (b['isSTEP'] && !a['isALTERNATE1'] && !a['isALTERNATE2']) {
		console.log('B is STEP');
		return 0;
	}
	if (a['isALTERNATE1'] && !b['isALTERNATE1'] && !b['isALTERNATE2']) {
		console.log('2');
		return 1;
	}
	if (a['isALTERNATE2'] && !b['isALTERNATE1'] && !b['isALTERNATE2']) {
		console.log('3');
		return 1;
	}
	if (b['isALTERNATE1'] && !a['isALTERNATE1'] && !a['isALTERNATE2']) {
		console.log('4');
		return -1;
	}
	if (b['isALTERNATE2'] && !a['isALTERNATE1'] && !a['isALTERNATE2']) {
		console.log('5');
		return -1;
	}
	if (a['isALTERNATE2'] && b['isSTEP']) {
		console.log('6');
		return 1;
	}
	if (a['isALTERNATE1'] && b['isSTEP']) {
		console.log('7');
		return 1;
	}
	if (b['isALTERNATE2'] && a['isSTEP']) {
		console.log('8');
		return 1;
	}
	if (b['isALTERNATE1'] && a['isSTEP']) {
		console.log('9');
		return 1;
	}
	if (a['isALTERNATE2'] && b['isALTERNATE1']) {
		console.log('10');
		return 1;
	}
	if (a['isALTERNATE2'] && b['isALTERNATE2']) {
		console.log('11');
		return 0;
	}
	if (a['calculated_event_time_in_seconds'] < b['calculated_event_time_in_seconds']) {
		console.log('12');
		return -1;
	}
	if (a['calculated_event_time_in_seconds'] > b['calculated_event_time_in_seconds']) {
		console.log('13');
		return 1;
	}
	return 0;
}

export function sortEventsForExport(events) {
	if (events == undefined) {
		return [];
	}

	var debug_loop_count = 0;
	var max_loops_allowed = events.length * 5; //Prevent infinite loops
	var made_a_change = true;

	while (made_a_change) {
		//Loop until a change is not made
		debug_loop_count++;
		made_a_change = false;

		for (var index = 1; index < events.length; index++) {
			var sorted = sortByCalculatedTime(events[index - 1], events[index]);
			if (sorted >= 1) {
				var temp_value = events[index - 1];
				events[index - 1] = events[index];
				events[index] = temp_value;
				made_a_change = true;
			}
		}
		if (debug_loop_count > max_loops_allowed) {
			made_a_change = false;
		}
	}
	return events;
}

export function updateShowType(events) {
	let show_has_STEP_events = !!events.find((event) => event.isSTEP == true);
	let show_has_timed_events = !!events.find((event) => event.isSTEP != true);
	let show_type = SHOW_TYPES_BY_EVENTS.TIMED;

	if (show_has_STEP_events == true && show_has_timed_events == true) {
		show_type = SHOW_TYPES_BY_EVENTS.COMPOUND;
	}
	if (show_has_STEP_events == true && show_has_timed_events == false) {
		show_type = SHOW_TYPES_BY_EVENTS.STEP;
	}
	if (show_has_STEP_events == false && show_has_timed_events == true) {
		show_type = SHOW_TYPES_BY_EVENTS.TIMED;
	}
	return show_type;
}

export function synchronizeEventTimesAndUpdateFlags(events) {
	for (const event of events) {
		if (event.timeline_event_time != event.event_time_in_seconds) {
			event.event_time_in_seconds = event.timeline_event_time;
			event.event_time = formatDisplayableTime(event.timeline_event_time);
		}
		// also update the converted_to_STEP_v2 flag true in the step event
		if (event.isSTEP == true && event.converted_to_STEP_v2 != true) {
			event.converted_to_STEP_v2 = true;
		} else if (event.isSTEP != true && event.converted_to_STEP_v2 == true) {
			delete event.converted_to_STEP_v2;
		}
	}
	return events;
}

export function getEventTypeColor() {
	if (this.event.isCOMMENT == true) {
		return 'bg-lime-600';
	}
	if (this.event.isSTEP == true) {
		return 'bg-blue-600';
	}
	if (this.event.isALTERNATE1 == true) {
		return 'bg-fuchsia-600';
	}
	if (this.event.isALTERNATE2 == true) {
		return 'bg-fuchsia-600';
	}

	return 'bg-cyan-600';
}

export function updateRelativeAndCalculatedTimesForAEvent({ event, adjusted_times }) {
	event.calculated_event_time = adjusted_times.event_time;
	event.calculated_event_time_in_seconds = adjusted_times.event_time_in_seconds;
	event.negative_calculated_event_time = adjusted_times.negative_calculated_time;

	event.relative_event_time = adjusted_times.relative_event_time;
	event.relative_event_time_in_seconds = adjusted_times.relative_event_time_in_seconds;
}

export function createStepParentChildRelationship({ start_from_row_index, is_control_view } = {}) {
	const show_store = useShowStore();
	const events = is_control_view === true ? useShowOperatorStore().events_list : show_store?.active_show?.events;

	if (events != undefined && events.length > 0) {
		let has_step_event_started = false;
		let current_step_event_id;
		for (let i = start_from_row_index ?? 0; i < events.length; i++) {
			const event = events[i];
			if (event.isSTEP == true || event.event_type == 'STEP') {
				has_step_event_started = true;
				current_step_event_id = event.event_id;
				delete event.parent_event;
				continue;
			}
			// validate if the current event is a child of step, then add parent_step_event_id to it
			if (has_step_event_started == true && event.isALTERNATE1 != true && event.isALTERNATE2 != true && event.event_type != 'ALTERNATE1' && event.event_type != 'ALTERNATE2') {
				// current_step_event_id can never be undefined here.
				// event.isSTEP can never be true here.
				event.parent_event = current_step_event_id;
				continue;
			}

			// fall through : if the current event is not a child of step, then delete parent_step_event_id
			delete event.parent_event;
		}
	}
}

export function reCalculateStepOffsets({ next_step_event_row_index, event_to_which_step_event_is_snapped_to, called_due_to_change_in_duration } = {}) {
	if (next_step_event_row_index == undefined || event_to_which_step_event_is_snapped_to == undefined) {
		return;
	}
	// offsets needs to be recalculated from next_step_event_row_index downwards
	// we can calculate the new absolute times of next_step_event_rows_index downwards with the help of new event_time of event_to_which_step_event_is_snapped_to
	const events = useShowStore()?.active_show?.events;
	let active_step;

	for (let i = next_step_event_row_index; i < events.length; i++) {
		const event = events[i];
		if (event.isALTERNATE1 == true || event.isALTERNATE2 == true) {
			continue;
		}
		event['method_used_to_edit_time'] = METHOD_TO_EDIT_TIME.STEP_EVENT_RECALCULATED;
		// If the event is STEP, make sure it now matches the time of last child of previous step
		if (event.isSTEP == true) {
			active_step = event;
			const previous_event_duration_in_seconds = event_to_which_step_event_is_snapped_to.duration_in_seconds;

			if (previous_event_duration_in_seconds > 0) {
				const previous_event_time_in_seconds = event_to_which_step_event_is_snapped_to.event_time_in_seconds + previous_event_duration_in_seconds;
				event.event_time = formatDisplayableTime(previous_event_time_in_seconds);
				event.event_time_in_seconds = previous_event_time_in_seconds;
				event.timeline_event_time = previous_event_time_in_seconds;
			} else {
				event.event_time = event_to_which_step_event_is_snapped_to.event_time;
				event.event_time_in_seconds = event_to_which_step_event_is_snapped_to.event_time_in_seconds;
				event.timeline_event_time = event_to_which_step_event_is_snapped_to.timeline_event_time;
			}

			event_to_which_step_event_is_snapped_to = event;
		} else if (active_step != undefined) {
			// means this event is the child of active_step
			const new_time_in_seconds = active_step.event_time_in_seconds + event.relative_event_time_in_seconds;
			event.event_time = formatDisplayableTime('' + new_time_in_seconds);
			event.event_time_in_seconds = new_time_in_seconds;
			event.timeline_event_time = new_time_in_seconds;

			// check if next event after this child is step if yes then its the last child
			const is_last_child = events.length - 1 > i ? events[i + 1].isSTEP == true : true;
			if (is_last_child == true) {
				const next_step = events[i + 1];
				// if (event.event_time_in_seconds <= next_step?.event_time_in_seconds) {
				// in case of called_due_to_change_in_duration, we don't want to break the loop
				if (event.event_time_in_seconds == next_step?.event_time_in_seconds && called_due_to_change_in_duration != true) {
					// to tackle the scenario where the event_time of child of top most step event is reduced, the reCalculation of relative_time and event_times of bottoms events were not updating..
					break;
				}
				event_to_which_step_event_is_snapped_to = event;
			}
		}
	}
}

// to reCalculate the offsets of either single timed event, or if any child event exceeds the boundary of its parent.
export function updateOffsetsAndRelativeTime({ updated_event, event_to_which_step_event_is_snapped_to, sorting_done_due_to_reduced_event_time, called_due_to_change_in_duration } = {}) {
	const show_store = useShowStore();

	// make sure that updated_event.index is correct
	if (updated_event.event_id != undefined) {
		updated_event.index = show_store.active_show.events.findIndex((e) => updated_event.event_id === e.event_id);
	}

	// add new check if updated_event is child of step, means have parent_event.
	// if yes then check if the new event_time of updated event exceeds the bounds of its parent event and enters in the bounds of NEXT STEP event, then make sure to recalculate the offsets
	// else continue
	let current_event_being_updated = show_store.active_show.events[updated_event.index];
	current_event_being_updated.index = updated_event.index;

	const parent_step_event_id = current_event_being_updated?.parent_event; // parent_event is event_id of parent step (if any)
	if (parent_step_event_id != undefined) {
		// get parent_row_index, it should never be undefined
		const parent_step = show_store.step_events.find((step_event) => step_event.event_info.event_id == parent_step_event_id);
		const parent_step_event_row_index = parent_step.row_index_of_step_event;

		// check if next step exists after this or not
		// if exists then find will fetch the exact next step, it maybe undefined.

		let next_step_event = show_store.step_events.find((step_event) => step_event.row_index_of_step_event > parent_step_event_row_index);
		const next_step_event_row_index = next_step_event?.row_index_of_step_event;
		next_step_event = next_step_event?.event_info;

		// || first condition is to check if the current event is just above the next step event, then snap it to next step event
		// || second condition is to check if the current event time is already above the next step event time, then snap it to current event
		if (next_step_event != undefined) {
			// get next step event_time_in_seconds, it should never be null at this stage
			// const next_step_event_time_in_seconds = show_store.active_show.events[next_step_event_row_index].event_time_in_seconds;

			let event_just_above_next_step_event = show_store.active_show.events[next_step_event_row_index - 1]; // this shall never be undefined/null at this stage
			const is_event_time_in_seconds_of_updated_event_greater_than_event_just_above_next_step_event = current_event_being_updated.event_time_in_seconds > event_just_above_next_step_event.event_time_in_seconds;
			if (event_to_which_step_event_is_snapped_to == undefined && is_event_time_in_seconds_of_updated_event_greater_than_event_just_above_next_step_event) {
				// means the current event time is greater than the next step event time. Then snap the next step to this event
				event_to_which_step_event_is_snapped_to = current_event_being_updated;
			}
			// evs-349 : this check is now removed, as next step should be snapped with the updated_time
			if ((next_step_event.event_time_in_seconds <= event_just_above_next_step_event.event_time_in_seconds && sorting_done_due_to_reduced_event_time == true) || !is_event_time_in_seconds_of_updated_event_greater_than_event_just_above_next_step_event) {
				// means offsets needs to be calculated.
				reCalculateStepOffsets({ next_step_event_row_index, event_to_which_step_event_is_snapped_to: event_just_above_next_step_event, called_due_to_change_in_duration });
			} else if (event_to_which_step_event_is_snapped_to != undefined) {
				reCalculateStepOffsets({ next_step_event_row_index, event_to_which_step_event_is_snapped_to, called_due_to_change_in_duration });
			}
		}

		// update the relative times
		const adjusted_times = calculateEventTimeForEvent(current_event_being_updated, show_store.active_show.firmware_version, parent_step.event_info.timeline_event_time);
		updateRelativeAndCalculatedTimesForAEvent({ event: current_event_being_updated, adjusted_times });
	} else {
		let next_step_event = undefined;
		let next_step_event_row_index = undefined;
		let current_event_index = show_store.active_show.events.findIndex((event) => event.event_id == current_event_being_updated.event_id);
		for (let i = current_event_index + 1; i < show_store.active_show.events.length; i++) {
			const event = show_store.active_show.events[i];
			if (event.isSTEP == true) {
				next_step_event_row_index = i;
				next_step_event = event;
				break;
			}
		}

		// || first condition is to check if the current event is just above the next step event, then snap it to next step event
		// || second condition is to check if the current event time is already above the next step event time, then snap it to current event

		// evs-349 : event_time of current event exceeded the step time check, as next step should be snapped with the updated_time
		// now to validate if the current updated_event is at the top most of the step event. If yes then reCalculate the offsets for next step event.
		if (next_step_event != undefined) {
			let event_just_above_next_step_event = show_store.active_show.events[next_step_event_row_index - 1]; // this shall never be undefined/null at this stage
			const is_event_time_in_seconds_of_updated_event_greater_than_event_just_above_next_step_event = current_event_being_updated.event_time_in_seconds > event_just_above_next_step_event.event_time_in_seconds;
			if (event_to_which_step_event_is_snapped_to == undefined && is_event_time_in_seconds_of_updated_event_greater_than_event_just_above_next_step_event) {
				// means the current event time is greater than the next step event time. Then snap the next step to this event
				event_to_which_step_event_is_snapped_to = current_event_being_updated;
			}

			if ((next_step_event.event_time_in_seconds <= event_just_above_next_step_event.event_time_in_seconds && sorting_done_due_to_reduced_event_time == true) || !is_event_time_in_seconds_of_updated_event_greater_than_event_just_above_next_step_event) {
				// means offsets needs to be calculated.
				reCalculateStepOffsets({ next_step_event_row_index, event_to_which_step_event_is_snapped_to: event_just_above_next_step_event, called_due_to_change_in_duration });
			} else if (event_to_which_step_event_is_snapped_to != undefined) {
				reCalculateStepOffsets({ next_step_event_row_index, event_to_which_step_event_is_snapped_to, called_due_to_change_in_duration });
			}
		}

		// update the relative times for non step child events
		const adjusted_times = calculateEventTimeForEvent(current_event_being_updated, show_store.active_show.firmware_version);
		updateRelativeAndCalculatedTimesForAEvent({ event: current_event_being_updated, adjusted_times });
	}
}

export function canEventTimeBeUpdated({ updated_event }) {
	const show_store = useShowStore();

	// make sure that updated_event.index is correct
	if (updated_event.event_id != undefined) {
		updated_event.index = show_store.active_show.events.findIndex((e) => updated_event.event_id === e.event_id);
	}

	const current_event_being_updated = show_store.active_show.events[updated_event.index];

	const parent_step_event_id = current_event_being_updated?.parent_event; // parent_event is event_id of parent step (if any)
	if (parent_step_event_id != undefined) {
		const parent_event = show_store.active_show.events.find((event) => event.event_id == parent_step_event_id); // this shall never be null at this stage.
		// Validate if the updated event is a step child, and its time is being reduced to less than the time of its parent. If yes, then don't allow.
		// Compare at millisecond precision so accumulated floating-point drift in the stored parent time (e.g. 290.28000000000003) does not reject a child set to the same displayed time.
		const child_time = Number(convertFormattedStringIntoSeconds(updated_event.event_time + '').toFixed(3));
		const parent_time = Number((parent_event.event_time_in_seconds ?? 0).toFixed(3));
		if (child_time < parent_time) {
			return false;
		}
	}

	return true;
}

export function getPreviousEventDetails(events, current_event_index) {
	let previous_event_time = undefined;
	let previous_event_time_in_seconds = undefined;
	let previous_event_duration_in_seconds = 0;

	for (let i = current_event_index - 1; i >= 0; i--) {
		const event = events[i];
		if (event.isALTERNATE !== true && event.isALTERNATE1 !== true && event.isALTERNATE2 !== true && event.isCOMMENT !== true) {
			previous_event_time = event.event_time;
			previous_event_time_in_seconds = event.event_time_in_seconds;
			previous_event_duration_in_seconds = event.duration_in_seconds;
			break;
		}
	}

	if (previous_event_time == undefined && previous_event_time_in_seconds == undefined) {
		previous_event_time = '00:00.00';
		previous_event_time_in_seconds = 0;
	}

	return { previous_event_time, previous_event_time_in_seconds: Number(previous_event_time_in_seconds), previous_event_duration_in_seconds: Number(previous_event_duration_in_seconds) };
}

export function updateCuesUsedPerChannel(events) {
	used_cues_per_channel = {};

	for (const event of events) {
		if (event.isCOMMENT == true) {
			continue;
		}

		let channel = parseInt(event.channel);
		let cue_value = parseInt(event.cue);
		if (isNaN(channel) || channel == undefined) {
			continue;
		}
		if (isNaN(cue_value) || cue_value == undefined) {
			continue;
		}

		if (used_cues_per_channel[channel] == undefined) {
			//Create array
			used_cues_per_channel[channel] = [cue_value];
		} else {
			used_cues_per_channel[channel].push(cue_value);
		}
	}

	const channel_summary = {};
	for (const ch of Object.keys(used_cues_per_channel)) {
		channel_summary[ch] = { count: used_cues_per_channel[ch].length, cues: used_cues_per_channel[ch].slice(0, 50) };
	}
	console.log('[EVS-981_CC_DEBUG] updateCuesUsedPerChannel rebuilt', {
		total_events_scanned: events?.length,
		channels: channel_summary,
	});
}

export function removeUsedCueFromChannel(previous_cue, event, show) {
	if (previous_cue != undefined) {
		if (used_cues[event.channel]?.[previous_cue] != undefined && used_cues[event.channel][previous_cue]?.indexOf(event.event_id) >= 0) {
			used_cues[event.channel][previous_cue].splice(used_cues[event.channel][previous_cue].indexOf(event.event_id), 1);
			if (used_cues[event.channel][previous_cue]?.length == 1) {
				const event_id_of_unique_event_left_in_channel_for_previous_cue = used_cues[event.channel][previous_cue][0];
				const unique_cue_event_in_channel_for_previous_cue_value = show.events.find((show_event) => event_id_of_unique_event_left_in_channel_for_previous_cue == show_event.event_id);
				if (unique_cue_event_in_channel_for_previous_cue_value != undefined) {
					calculateNumberOfCuesUsedForChannel(show.firmware_version);
					checkForRefiredEvents(unique_cue_event_in_channel_for_previous_cue_value, show.firmware_version);
				}
			}
		}
	}
}

export function removeUsedChannelFromUsedCues(previous_channel, event, show) {
	if (previous_channel != undefined) {
		if (used_cues[previous_channel][event.cue]?.indexOf(event.event_id) >= 0) {
			used_cues[previous_channel][event.cue].splice(used_cues[previous_channel][event.cue].indexOf(event.event_id), 1);
			if (used_cues[previous_channel] != undefined && used_cues[previous_channel][event.cue]?.length == 1) {
				const event_id_of_unique_event_left_in_channel_for_previous_channel = used_cues[previous_channel][event.cue][0];
				const unique_cue_event_in_channel_for_previous_channel_value = show.events.find((show_event) => event_id_of_unique_event_left_in_channel_for_previous_channel == show_event.event_id);
				if (unique_cue_event_in_channel_for_previous_channel_value != undefined) {
					calculateNumberOfCuesUsedForChannel(show.firmware_version);
					checkForRefiredEvents(unique_cue_event_in_channel_for_previous_channel_value, show.firmware_version);
				}
			}
		}
	}
}

export function getNextCueForChannel(channel) {
	const raw_input_channel = channel;
	const raw_input_type = typeof channel;

	// Check for invalid channel input
	if (channel == undefined || null) {
		console.log('[EVS-981_CC_DEBUG] getNextCueForChannel returning undefined (null/undefined input)', { raw_input_channel, raw_input_type });
		return undefined;
	}

	// If the channel is a string, attempt to convert it to an integer
	if (typeof channel == 'string') {
		channel = parseInt(channel);
		// Return undefined if the conversion fails or if the resulting channel is undefined
		if (isNaN(channel) || channel == undefined) {
			console.log('[EVS-981_CC_DEBUG] getNextCueForChannel returning undefined (NaN after parseInt)', { raw_input_channel, raw_input_type });
			return undefined;
		}
	}

	// If no cues have been used for the given channel, return 1 as the first available cue
	if (used_cues_per_channel[channel] == undefined || used_cues_per_channel[channel] == null) {
		console.log('[EVS-981_CC_DEBUG] getNextCueForChannel returning 1 (channel has no used cues)', { raw_input_channel, raw_input_type, parsed_channel: channel });
		return 1;
	}

	// Define arrays of fillable and available cues
	let fillable_cues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
	let available_cues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

	// Determine how many times the fillable cues need to be added based on the used cues
	for (let i = 1; i < Math.ceil(used_cues_per_channel[channel].length / fillable_cues.length); i++) {
		available_cues.push(...fillable_cues);
	}

	// Iterate through the used cues and update the available cues array
	for (const used_cue of used_cues_per_channel[channel]) {
		let index = available_cues.indexOf(used_cue);

		if (index == -1) {
			// If the used cue is missing, add another set of 18 cues
			available_cues.push(...fillable_cues);
			index = available_cues.indexOf(used_cue);
		}

		if (index != undefined && index != -1) {
			// Remove the used cue from the available cues
			available_cues.splice(index, 1);
		}

		// If all cues have been used, restart the available cues
		if (available_cues.length == 0) {
			available_cues = JSON.parse(JSON.stringify(fillable_cues));
		}
	}

	console.log('[EVS-981_CC_DEBUG] getNextCueForChannel returning', {
		raw_input_channel,
		raw_input_type,
		parsed_channel: channel,
		used_cues_for_channel: used_cues_per_channel[channel],
		used_cues_count: used_cues_per_channel[channel].length,
		first_remaining_available: available_cues.slice(0, 10),
		returned_cue: available_cues[0],
	});

	// Return the first available cue
	return available_cues[0];
}

export function syncSnappingOfStepEventsAfterPreviousEventIsEnded() {
	const show_store = useShowStore();
	// verify if show has any step events
	let first_step_event_row_index = show_store.step_events?.[0]?.row_index_of_step_event ?? undefined;
	if (first_step_event_row_index == undefined) {
		return;
	}

	let event_to_be_snapped_to = first_step_event_row_index - 1;
	if (event_to_be_snapped_to >= 0) {
		event_to_be_snapped_to = show_store.active_show.events[first_step_event_row_index - 1];
	} else {
		event_to_be_snapped_to = show_store.active_show.events[0];
	}

	const previous_event_of_first_step_event = event_to_be_snapped_to;
	updateOffsetsAndRelativeTime({ updated_event: previous_event_of_first_step_event, event_to_which_step_event_is_snapped_to: undefined, sorting_done_due_to_reduced_event_time: false, called_due_to_change_in_duration: true });
}
